import type { Location, NavigateFunction } from 'react-router-dom'
import * as R from 'ramda'
import type { Dispatch } from 'redux'

import { ConversationTransport } from '~/api/graphql/generated/types'
import { ConversationStatus } from '~/constants/communications'

import { messageFormattingAreaFocused } from '../actions/conversationMessages'

interface ConversationFormattingStateParams {
  canSendSmsToClient: boolean
  hasLatestMessage: boolean
  isLatestMessageInbound: boolean
  isLogicalSMSThreadingEnabled: boolean
  isMarkMessagesAsMedicalEnabled: boolean
  isUnifiedConversationTransportationThreadingEnabled: boolean
  messageFormattingAreaExpanded: boolean
  status: string | null | undefined
  totalCount: number
  transport: string | null | undefined
}

export const getConversationFormattingState = ({
  status,
  transport,
  isUnifiedConversationTransportationThreadingEnabled,
  isLogicalSMSThreadingEnabled,
  isMarkMessagesAsMedicalEnabled,
  messageFormattingAreaExpanded,
  canSendSmsToClient,
  isLatestMessageInbound,
  totalCount,
  hasLatestMessage,
}: ConversationFormattingStateParams) => {
  const isDeliveryFailed = status === ConversationStatus.DELIVERY_FAILED
  const isArchived = status === ConversationStatus.ARCHIVED
  const showOnlyOpenThreadButton =
    isUnifiedConversationTransportationThreadingEnabled && isArchived
  const isSmsTransport = transport === ConversationTransport.Sms
  const internalNoteOnly = transport === ConversationTransport.LogPhoneCall
  const canSendMessage = isSmsTransport ? canSendSmsToClient : !internalNoteOnly
  const showSendButton =
    messageFormattingAreaExpanded || !isMarkMessagesAsMedicalEnabled
  const showReplyButton =
    (isMarkMessagesAsMedicalEnabled || isLogicalSMSThreadingEnabled) &&
    !messageFormattingAreaExpanded &&
    !internalNoteOnly &&
    !showOnlyOpenThreadButton
  const showReplyInNewThreadButton =
    isLogicalSMSThreadingEnabled &&
    totalCount > 1 &&
    hasLatestMessage &&
    isLatestMessageInbound &&
    !showOnlyOpenThreadButton

  return {
    isDeliveryFailed,
    internalNoteOnly,
    canSendMessage,
    showSendButton,
    showReplyButton,
    showReplyInNewThreadButton,
  }
}

interface ArchiveActionTitleParams {
  isLogicalSMSThreadingEnabled: boolean
  status: string | null | undefined
  t: (key: string) => string
}

export const getArchiveActionTitle = ({
  status,
  isLogicalSMSThreadingEnabled,
  t,
}: ArchiveActionTitleParams): string => {
  const archivedTitle = isLogicalSMSThreadingEnabled
    ? t('Communications:MESSAGE_FORMATTING_AREA.ARCHIVE_THREAD')
    : t('Communications:MESSAGE_FORMATTING_AREA.ARCHIVE_CONVERSATION')

  const openTitle = isLogicalSMSThreadingEnabled
    ? t('Communications:MESSAGE_FORMATTING_AREA.OPEN_THREAD')
    : t('Common:OPEN_CONVERSATION')

  return status === ConversationStatus.ARCHIVED ? openTitle : archivedTitle
}

export const syncIsMedicalValue = (
  editMessageIsMedical: boolean | undefined,
  defaultCheckboxValue: boolean,
  setIsMedicalValue: (value: boolean) => void,
) => {
  if (!R.isNil(editMessageIsMedical)) {
    setIsMedicalValue(editMessageIsMedical)
  } else {
    setIsMedicalValue(defaultCheckboxValue)
  }
}

export const handleInitialLocationState = (
  location: Location,
  dispatch: Dispatch,
  navigate: NavigateFunction,
  closeFormattingArea: () => void,
) => {
  const openFromNewThreadReply = (
    location.state as { openMessageFormattingArea?: boolean } | null
  )?.openMessageFormattingArea

  if (openFromNewThreadReply) {
    dispatch(messageFormattingAreaFocused())
    navigate(location.pathname, { replace: true, state: {} })
  } else {
    closeFormattingArea()
  }
}
